import axiosInstance from './axiosInstance';

export interface ProjectAssetCheckpoint {
  _id?: string; // Optional because backend might return 'id' instead
  id?: string;  // Backend returns 'id' field
  name: string;
  frequency: 'Daily' | 'Weekly' | 'Fortnightly' | 'Monthly' | 'Quarterly' | 'Half-Yearly' | 'Yearly';
  priority: 'Low' | 'Medium' | 'High' | 'Critical';
  status: 'active' | 'inactive';
  requirement?: string;
  // Grace period in days - how many days after due date before checkpoint becomes overdue
  bufferTimeDays?: number;

  // New Attributes for Task Execution Logic
  description?: string;
  taskType?: ('Visual' | 'Hearing' | 'Manual')[];
  isImageRequired?: boolean;
  isInputRequired?: boolean;
  executionPhase?: string;
  executionTime?: string;

  // Advanced scheduling configuration
  schedulingConfig?: {
    scheduleType: 'auto' | 'custom';
    customSchedule?: {
      startDate?: string;
      endDate?: string;
      executionType: 'one-time' | 'repeated';
      customFrequency?: 'Daily' | 'Weekly' | 'Fortnightly' | 'Monthly' | 'Quarterly' | 'Half-Yearly' | 'Yearly';
      weeklyDays?: number[];
      monthlyDay?: number;
      yearlyMonths?: number[];
      executionTime?: string; // HH:MM format
      timezone?: string;
      skipWeekends?: boolean;
      skipHolidays?: boolean;
      holidayCalendar?: string;
    };
  };

  projectTypeId?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProjectAsset {
  _id: string;
  id?: string; // toJSON-transformed responses expose 'id' (see ProjectAssetCheckpoint note)
  name: string;
  description?: string;
  code?: string; // Asset code from master asset (e.g., "EMU", "INV", "STR")
  masterAssetId: string;
  projectId: string;
  status: 'active' | 'inactive';
  checkpoints?: ProjectAssetCheckpoint[];
  totalCheckpoints?: number; // Added from with-checkpoints endpoint
  equipmentCount?: number;   // Added from with-checkpoints endpoint
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateProjectAssetCheckpointData {
  name: string;
  frequency: string;
  requirement?: string;
  priority?: string;
  bufferTimeDays?: number; // Grace period in days
  // New Attributes for Task Execution Logic
  description?: string;
  taskType?: ('Visual' | 'Hearing' | 'Manual')[];
  isImageRequired?: boolean;
  isInputRequired?: boolean;
  executionPhase?: string;
  executionTime?: string;
  schedulingConfig?: {
    scheduleType: 'auto' | 'custom';
    customSchedule?: {
      startDate?: string;
      endDate?: string;
      executionType: 'one-time' | 'repeated';
      customFrequency?: string;
      weeklyDays?: number[];
      monthlyDay?: number;
      yearlyMonths?: number[];
      executionTime?: string;
      timezone?: string;
      skipWeekends?: boolean;
      skipHolidays?: boolean;
      holidayCalendar?: string;
    };
  };
  // projectTypeId removed - no longer required on backend
}

export interface UpdateProjectAssetCheckpointData {
  name?: string;
  frequency?: string;
  requirement?: string;
  priority?: string;
  bufferTimeDays?: number; // Grace period in days
  // New Attributes for Task Execution Logic
  description?: string;
  taskType?: ('Visual' | 'Hearing' | 'Manual')[];
  isImageRequired?: boolean;
  isInputRequired?: boolean;
  executionPhase?: string;
  executionTime?: string;
  schedulingConfig?: {
    scheduleType: 'auto' | 'custom';
    customSchedule?: {
      startDate?: string;
      endDate?: string;
      executionType: 'one-time' | 'repeated';
      customFrequency?: string;
      weeklyDays?: number[];
      monthlyDay?: number;
      yearlyMonths?: number[];
      executionTime?: string;
      timezone?: string;
      skipWeekends?: boolean;
      skipHolidays?: boolean;
      holidayCalendar?: string;
    };
  };
  status?: 'active' | 'inactive';
}

class ProjectAssetService {
  /**
   * Normalize checkpoint data to ensure _id is always available
   */
  private normalizeCheckpointData(checkpoint: any): any {
    if (checkpoint && !checkpoint._id && checkpoint.id) {
      return { ...checkpoint, _id: checkpoint.id };
    }
    return checkpoint;
  }

  /**
   * Normalize project asset data to ensure checkpoints have proper _id
   */
  private normalizeProjectAssetData(projectAsset: any): any {
    if (projectAsset && projectAsset.checkpoints) {
      return {
        ...projectAsset,
        checkpoints: projectAsset.checkpoints.map((cp: any) => this.normalizeCheckpointData(cp))
      };
    }
    return projectAsset;
  }

  /**
   * Normalize project asset to ensure _id exists
   */
  private normalizeProjectAsset(asset: any): ProjectAsset {
    if (asset && !asset._id && asset.id) {
      return { ...asset, _id: asset.id };
    }
    return asset;
  }

  /**
   * Create or get project asset by project ID and master asset ID
   */
  async createOrGetProjectAsset(projectId: string, masterAssetId: string, selectedCheckpointIds?: string[]): Promise<ProjectAsset> {
    const response = await axiosInstance.post('/project-assets', {
      projectId,
      masterAssetId,
      selectedCheckpointIds
    });
    const asset = response.data.data;
    console.log('createOrGetProjectAsset response:', asset);
    return this.normalizeProjectAsset(asset);
  }

  /**
   * Get project asset by project ID and master asset ID
   */
  async getProjectAssetByProjectAndMasterAsset(projectId: string, masterAssetId: string): Promise<ProjectAsset | null> {
    const response = await axiosInstance.get('/project-assets/by-project-and-master', {
      params: {
        projectId,
        masterAssetId
      }
    });
    return response.data.data; // This will be null if project asset doesn't exist
  }

  /**
   * Get project asset by project ID and asset name
   */
  async getProjectAssetByProjectAndAssetName(projectId: string, assetName: string): Promise<ProjectAsset> {
    const response = await axiosInstance.get('/project-assets/by-name', {
      params: {
        projectId,
        assetName
      }
    });
    return this.normalizeProjectAssetData(response.data.data);
  }

  /**
   * Get project asset with checkpoints
   */
  async getProjectAssetWithCheckpoints(projectAssetId: string): Promise<ProjectAsset> {
    const response = await axiosInstance.get(`/project-assets/${projectAssetId}`);
    return this.normalizeProjectAssetData(response.data.data);
  }

  /**
   * Get all project assets for a project
   */
  async getProjectAssetsByProject(projectId: string): Promise<ProjectAsset[]> {
    const response = await axiosInstance.get(`/project-assets/by-project/${projectId}`);
    return response.data.data.map((asset: any) => this.normalizeProjectAsset(this.normalizeProjectAssetData(asset)));
  }

  /**
   * Create a new checkpoint for a project asset
   */
  async createProjectAssetCheckpoint(
    projectAssetId: string,
    checkpointData: CreateProjectAssetCheckpointData
  ): Promise<ProjectAssetCheckpoint> {
    const response = await axiosInstance.post(`/project-assets/${projectAssetId}/checkpoints`, checkpointData);
    return response.data.data;
  }

  /**
   * Update a project asset checkpoint
   */
  async updateProjectAssetCheckpoint(
    checkpointId: string,
    updateData: UpdateProjectAssetCheckpointData
  ): Promise<ProjectAssetCheckpoint> {
    const response = await axiosInstance.put(`/project-assets/checkpoints/${checkpointId}`, updateData);
    return response.data.data;
  }

  /**
   * Update project asset checkpoint status (activate/deactivate)
   */
  async updateProjectAssetCheckpointStatus(
    checkpointId: string,
    status: 'active' | 'inactive'
  ): Promise<ProjectAssetCheckpoint> {
    const response = await axiosInstance.put(`/project-assets/checkpoints/${checkpointId}/status`, { status });
    return response.data.data;
  }

  /**
   * Delete a project asset checkpoint (soft delete)
   */
  async deleteProjectAssetCheckpoint(checkpointId: string): Promise<ProjectAssetCheckpoint> {
    const response = await axiosInstance.delete(`/project-assets/checkpoints/${checkpointId}`);
    return response.data.data;
  }

  /**
   * Get checkpoints for a project asset
   */
  async getProjectAssetCheckpoints(projectAssetId: string): Promise<ProjectAssetCheckpoint[]> {
    const response = await axiosInstance.get(`/project-assets/${projectAssetId}/checkpoints`);
    return response.data.data;
  }

  /**
   * Get all checkpoints for a project asset including inactive ones
   */
  async getAllProjectAssetCheckpoints(projectAssetId: string): Promise<ProjectAssetCheckpoint[]> {
    const response = await axiosInstance.get(`/project-assets/${projectAssetId}/checkpoints`, {
      params: { includeInactive: true }
    });
    return response.data.data;
  }

  /**
   * Get project asset by ID
   */
  async getProjectAssetById(projectAssetId: string): Promise<ProjectAsset> {
    const response = await axiosInstance.get(`/project-assets/${projectAssetId}`);
    return response.data.data;
  }

  /**
   * Get equipment by project asset
   */
  async getEquipmentByProjectAsset(projectAssetId: string): Promise<any[]> {
    const response = await axiosInstance.get(`/project-assets/${projectAssetId}/equipment`);
    return response.data.data;
  }

  /**
   * Update project asset status
   */
  async updateProjectAssetStatus(projectAssetId: string, status: string): Promise<ProjectAsset> {
    const response = await axiosInstance.put(`/project-assets/${projectAssetId}/status`, { status });
    return this.normalizeProjectAssetData(response.data.data);
  }

  /**
   * Get project assets with details (checkpoints and counts)
   */
  async getProjectAssetsWithDetails(projectId: string): Promise<any[]> {
    const response = await axiosInstance.get('/project-assets/with-checkpoints', {
      params: { projectId }
    });
    return response.data.data;
  }
}

export default new ProjectAssetService();
